import { Store } from '@gozoki/api-types';
import jsPDF from 'jspdf';
import { DateTime } from 'luxon';
import logo from '../../assets/logos/logo.png';
import type { ProductWithCount } from '../../pages/dashboard/DashboardPage';
import {
    calculateProductDifference,
    calculateTotals,
    groupProductsByCategory,
    prepareReceptionData,
    prepareReceptionDataForPeriod,
    type ProductWithCountAndNeeded,
    type ReceptionTotals,
} from '../excel/prepareReceptionData';

// =============================================================================
// LAYOUT CONSTANTS
// =============================================================================

const LAYOUT = {
    PAGE_HEIGHT: 297, // A4 height in mm
    HEADER_END: 100, // Where the product table starts (after table header line at 93)
    FOOTER_START: 280, // Where footer begins
    CONTENT_MARGIN: 10, // Left/right margin
    LINE_HEIGHT: 8, // Height of a product line
    CATEGORY_HEADER_HEIGHT: 8,
    CATEGORY_TOTAL_HEIGHT: 32, // 4 lines: total + surplus + missing + service rate
} as const;

const COLUMNS = {
    LABEL: 15,
    EAN13: 70,
    NEEDED: 125,
    DELIVERED: 155,
    DIFFERENCE: 180,
} as const;

// =============================================================================
// FONTS
// =============================================================================

type FontStyle = { name: string; style: string; size: number; color: 'black' | 'grey' };

const FONTS = {
    TITLE: { name: 'helvetica', style: 'bold', size: 18, color: 'black' } as FontStyle,
    SUBTITLE: { name: 'helvetica', style: 'bold', size: 14, color: 'grey' } as FontStyle,
    BOLD: { name: 'helvetica', style: 'bold', size: 10, color: 'black' } as FontStyle,
    NORMAL: { name: 'helvetica', style: 'normal', size: 10, color: 'black' } as FontStyle,
    SMALL: { name: 'helvetica', style: 'normal', size: 8, color: 'black' } as FontStyle,
    GREY: { name: 'helvetica', style: 'normal', size: 9, color: 'grey' } as FontStyle,
};

const setFont = (doc: jsPDF, font: FontStyle) => {
    doc.setFont(font.name, font.style);
    doc.setFontSize(font.size);
    doc.setTextColor(font.color === 'grey' ? 128 : 0);
};

// =============================================================================
// PDF DRAWING FUNCTIONS
// =============================================================================

const drawHeader = (
    doc: jsPDF,
    pageNum: number,
    storeInfo: { label: string; address1: string; address2: string; address3: string },
    receptionDate: DateTime
) => {
    // Title and logo
    setFont(doc, FONTS.TITLE);
    doc.text('BON DE RECEPTION', 15, 15);
    setFont(doc, FONTS.NORMAL);
    doc.text(`(Page ${pageNum})`, 80, 15);
    doc.addImage(logo, 'PNG', 170, 8, 27.5, 16);

    // Store name
    setFont(doc, FONTS.SUBTITLE);
    doc.text(storeInfo.label, 15, 22);

    // Reception info
    setFont(doc, FONTS.BOLD);
    doc.text('Numéro de bon:', 15, 31);
    doc.text('Date de réception:', 80, 31);
    setFont(doc, FONTS.GREY);
    doc.text(receptionDate.toFormat('dd/MM/yyyy'), 80, 36);
    doc.line(10, 39, 200, 39);

    // Sender/Receiver info
    setFont(doc, FONTS.BOLD);
    doc.text('Destinataire', 125, 44);
    setFont(doc, FONTS.GREY);
    doc.text('LUCIE Ultrafrais', 125, 49);
    setFont(doc, FONTS.SMALL);
    doc.text(storeInfo.address1, 125, 54);
    doc.text(storeInfo.address2, 125, 59);
    doc.text(storeInfo.address3, 125, 64);

    // Company info
    setFont(doc, FONTS.SMALL);
    doc.text('SIRET: 977 672 732 000012', 15, 69);
    doc.text('NAF : 4799B', 15, 74);
    doc.text('N° intracommunautaire : FR11 977 672 732', 15, 79);
    doc.line(10, 83, 200, 83);

    // Table header
    setFont(doc, FONTS.BOLD);
    doc.text('Nom du produit', COLUMNS.LABEL, 90);
    doc.text('EAN 13', COLUMNS.EAN13, 90);
    doc.text('Demandés', COLUMNS.NEEDED, 90);
    doc.text('Livrés', COLUMNS.DELIVERED, 90);
    doc.text('Ecart', COLUMNS.DIFFERENCE, 90);
    doc.line(10, 93, 200, 93);
};

const drawFooter = (doc: jsPDF, pageNum: number, totalPages: number) => {
    setFont(doc, FONTS.GREY);
    doc.text(`Page ${pageNum} sur ${totalPages}`, 15, 284);
    doc.text('06 83 24 67 23', 120, 284);
    doc.text('|', 154, 284);
    doc.text('contact@lucie-cabane.fr', 164, 284);
};

const drawCategoryHeader = (doc: jsPDF, y: number, categoryName: string) => {
    setFont(doc, FONTS.BOLD);
    doc.text(categoryName.toUpperCase(), COLUMNS.LABEL + COLUMNS.EAN13 - 15, y);
};

const drawProductLine = (doc: jsPDF, y: number, product: ProductWithCountAndNeeded) => {
    setFont(doc, FONTS.GREY);
    const label = (product.label || 'Unknown').slice(0, 32);
    const reference = product.reference?.replace(/\s+/g, '') || 'N/A';
    const diff = calculateProductDifference(product);
    const diffText = diff > 0 ? `+${diff}` : diff.toString();

    doc.text(label, COLUMNS.LABEL, y);
    doc.text(reference, COLUMNS.EAN13, y);
    doc.text((product.needed ?? 0).toString(), COLUMNS.NEEDED, y);
    doc.text((product.count ?? 0).toString(), COLUMNS.DELIVERED, y);
    doc.text(diffText, COLUMNS.DIFFERENCE, y);
    doc.line(10, y + 3, 200, y + 3);
};

const drawCategoryTotal = (
    doc: jsPDF,
    y: number,
    categoryName: string,
    totals: ReceptionTotals
) => {
    setFont(doc, FONTS.BOLD);

    // Main total line
    doc.text(`Total ${categoryName}`, COLUMNS.LABEL, y);
    doc.text(totals.totalNeeded.toFixed(2), COLUMNS.NEEDED, y);
    doc.text(totals.totalDelivered.toFixed(2), COLUMNS.DELIVERED, y);
    doc.text(totals.totalDifference.toFixed(2), COLUMNS.DIFFERENCE, y);
    doc.line(10, y + 3, 200, y + 3);

    // Surplus line
    doc.text(`Surplus ${categoryName}`, COLUMNS.LABEL, y + 8);
    doc.text(totals.totalSurplus.toFixed(2), COLUMNS.DIFFERENCE, y + 8);

    // Missing line
    doc.text(`Manquant ${categoryName}`, COLUMNS.LABEL, y + 16);
    doc.text(totals.totalMissing.toFixed(2), COLUMNS.DIFFERENCE, y + 16);

    // Service rate line
    doc.text(`Taux de service ${categoryName}`, COLUMNS.LABEL, y + 24);
    doc.text(`${(totals.serviceRate * 100).toFixed(1)}%`, COLUMNS.DIFFERENCE, y + 24);
};

const drawPageTotal = (
    doc: jsPDF,
    y: number,
    delivered: number,
    needed: number,
    difference: number
) => {
    setFont(doc, FONTS.BOLD);
    doc.text('Total page :', COLUMNS.LABEL, y);
    doc.text(needed.toFixed(2), COLUMNS.NEEDED, y);
    doc.text(delivered.toFixed(2), COLUMNS.DELIVERED, y);
    doc.text(difference.toFixed(2), COLUMNS.DIFFERENCE, y);
};

const drawFinalTotals = (doc: jsPDF, y: number, totals: ReceptionTotals) => {
    // Main total
    setFont(doc, FONTS.BOLD);
    doc.text('Total', COLUMNS.LABEL, y);
    doc.text(totals.totalNeeded.toFixed(2), COLUMNS.NEEDED, y);
    doc.text(totals.totalDelivered.toFixed(2), COLUMNS.DELIVERED, y);
    doc.text(totals.totalDifference.toFixed(2), COLUMNS.DIFFERENCE, y);

    // Surplus
    doc.text('Total surplus', COLUMNS.LABEL, y + 8);
    doc.text(totals.totalSurplus.toFixed(2), COLUMNS.DIFFERENCE, y + 8);

    // Missing
    doc.text('Total manquant', COLUMNS.LABEL, y + 16);
    doc.text(totals.totalMissing.toFixed(2), COLUMNS.DIFFERENCE, y + 16);

    // Service rate
    doc.text('Taux de service', COLUMNS.LABEL, y + 24);
    doc.text(`${(totals.serviceRate * 100).toFixed(1)}%`, COLUMNS.DIFFERENCE, y + 24);
};

// =============================================================================
// MAIN PDF GENERATOR
// =============================================================================

interface PageState {
    y: number;
    pageDelivered: number;
    pageNeeded: number;
    pageDifference: number; // Sum of absolute individual differences
}

class ReceptionPdfGenerator {
    private doc: jsPDF;
    private storeInfo: { label: string; address1: string; address2: string; address3: string };
    private receptionDate: DateTime;
    private currentPage = 1;
    private totalPages = 1;
    private state: PageState = {
        y: LAYOUT.HEADER_END,
        pageDelivered: 0,
        pageNeeded: 0,
        pageDifference: 0,
    };

    private currentCategoryName = ''; // Track current category for page breaks

    constructor(
        storeInfo: { label: string; address1: string; address2: string; address3: string },
        receptionDate: DateTime
    ) {
        // eslint-disable-next-line new-cap
        this.doc = new jsPDF();
        this.doc.setDrawColor(128);
        this.storeInfo = storeInfo;
        this.receptionDate = receptionDate;
    }

    private get maxY(): number {
        return LAYOUT.FOOTER_START - 25; // Leave space for page total
    }

    private needsNewPage(linesNeeded: number = 1): boolean {
        return this.state.y + linesNeeded * LAYOUT.LINE_HEIGHT > this.maxY;
    }

    private startNewPage(continuingCategory: boolean = false): void {
        // Draw page total before leaving
        drawPageTotal(
            this.doc,
            this.state.y + 4,
            this.state.pageDelivered,
            this.state.pageNeeded,
            this.state.pageDifference
        );

        this.doc.addPage();
        this.currentPage++;
        this.state = { y: LAYOUT.HEADER_END, pageDelivered: 0, pageNeeded: 0, pageDifference: 0 };
        drawHeader(this.doc, this.currentPage, this.storeInfo, this.receptionDate);

        // If continuing a category, repeat the header with "(suite)"
        if (continuingCategory && this.currentCategoryName) {
            drawCategoryHeader(this.doc, this.state.y, `${this.currentCategoryName} (suite)`);
            this.state.y += LAYOUT.CATEGORY_HEADER_HEIGHT;
        }
    }

    private addProduct(product: ProductWithCountAndNeeded): void {
        if (this.needsNewPage()) {
            this.startNewPage(true); // true = continuing a category
        }
        drawProductLine(this.doc, this.state.y, product);
        this.state.y += LAYOUT.LINE_HEIGHT;
        this.state.pageDelivered += product.count;
        this.state.pageNeeded += product.needed;
        this.state.pageDifference += Math.abs(calculateProductDifference(product));
    }

    private addCategoryHeader(name: string): void {
        this.currentCategoryName = name; // Track for page breaks
        if (this.needsNewPage()) {
            this.startNewPage(false); // false = starting fresh category
        }
        drawCategoryHeader(this.doc, this.state.y, name);
        this.state.y += LAYOUT.CATEGORY_HEADER_HEIGHT;
    }

    private addCategoryTotal(name: string, totals: ReceptionTotals): void {
        // Category total needs 4 lines worth of space
        if (this.needsNewPage(4)) {
            this.startNewPage();
        }
        drawCategoryTotal(this.doc, this.state.y, name, totals);
        this.state.y += LAYOUT.CATEGORY_TOTAL_HEIGHT;
    }

    generate(products: ProductWithCountAndNeeded[]): void {
        const categories = groupProductsByCategory(products);

        // First pass: calculate total pages (rough estimate for footer)
        const estimatedLines = products.length + categories.length * 2; // products + headers + totals
        const linesPerPage = Math.floor((this.maxY - LAYOUT.HEADER_END) / LAYOUT.LINE_HEIGHT);
        this.totalPages = Math.max(1, Math.ceil(estimatedLines / linesPerPage));

        // Draw first page header
        drawHeader(this.doc, this.currentPage, this.storeInfo, this.receptionDate);

        // Process each category
        categories.forEach((category) => {
            // Category header
            this.addCategoryHeader(category.name);

            // Products in category
            category.products.forEach((product) => {
                this.addProduct(product);
            });

            // Category total (with surplus, missing, and service rate)
            const categoryTotals = calculateTotals(category.products);
            this.addCategoryTotal(category.name, categoryTotals);
        });

        // Calculate final totals
        const totals = calculateTotals(products);

        // Check if there's enough space for page total + final totals (about 48mm needed)
        // Page total at y+4, final totals at y+16 spanning 32mm, so we need y+48 < footer
        const spaceNeededForTotals = 48;
        if (this.state.y + spaceNeededForTotals > LAYOUT.FOOTER_START - 10) {
            // Not enough space, start a new page
            this.startNewPage(false);
        }

        // Draw page total for last page
        drawPageTotal(
            this.doc,
            this.state.y + 4,
            this.state.pageDelivered,
            this.state.pageNeeded,
            this.state.pageDifference
        );

        // Draw final totals
        drawFinalTotals(this.doc, this.state.y + 16, totals);

        // Update total pages and draw footers on all pages
        this.totalPages = this.currentPage;
        Array.from({ length: this.totalPages }, (_, i) => i + 1).forEach((pageNum) => {
            this.doc.setPage(pageNum);
            drawFooter(this.doc, pageNum, this.totalPages);
        });
    }

    save(filename: string): void {
        this.doc.save(filename);
    }
}

// =============================================================================
// PUBLIC API
// =============================================================================

export const generateReceptionPdf = async (
    productsWithCount: ProductWithCount[],
    receivingStore: Store,
    receptionDate: DateTime,
    inventoryDates?: DateTime[]
): Promise<void> => {
    // Prepare data - aggregate needs across dates if multiple inventories
    const productsWithCountAndNeeded = inventoryDates
        ? await prepareReceptionDataForPeriod(productsWithCount, receivingStore, inventoryDates)
        : await prepareReceptionData(productsWithCount, receivingStore, receptionDate);

    // Build store info
    const storeInfo = {
        label: receivingStore.label ?? '',
        address1: receivingStore.address.address ?? '',
        address2: `${receivingStore.address.postalCode ?? ''} ${receivingStore.address.city ?? ''}`,
        address3: receivingStore.address.addressAdditionnal ?? 'France',
    };

    // Generate PDF
    const generator = new ReceptionPdfGenerator(storeInfo, receptionDate);
    generator.generate(productsWithCountAndNeeded);

    // Save with formatted filename
    const sanitizedStoreName = receivingStore.label.replace(/\s+/g, '');
    generator.save(`reception-${sanitizedStoreName}-${receptionDate.toFormat('yyyy-MM-dd')}.pdf`);
};
